/**
 * Wspólna logika wyznaczania dopuszczalnych mas na osiach (Sekcja 16.2/17.2 -
 * Technically permissible maximum mass on each axle), niezależna od konkretnej
 * zabudowy. Same tabele danych (per zabudowa) mieszkają w osobnych plikach -
 * IVECO_AXLE_MASS_LIMITS (iveco-axle-mass-limits.ts),
 * MERCEDES_TOURIST_LINE_HIGHDECK_AXLE_MASS_LIMITS
 * (mercedes-tourist-line-highdeck-axle-mass-limits.ts) - i są tu tylko
 * agregowane pod kluczem VehicleLine.
 *
 * Drugi poziom klucza w tabeli (oznaczenie skrzyni/napędu) jest opcjonalny -
 * gdy dana zabudowa go nie potrzebuje (dziś: Mercedes HighDeck, wartości nie
 * różnią się wg skrzyni/napędu), wpis trzyma się pod kluczem "*", tym samym
 * wzorcem "brak drugiego wymiaru" co przy danych masowych (patrz "*" w
 * vehicle-mass-data.ts).
 *
 * Dopóki dla danej zabudowy nie ma zarejestrowanej tabeli (np. brak jeszcze
 * źródła dla danego tonażu), getAxleMassLimit zwraca null, a
 * hasAxleMassLimitTable false - dzięki temu logika w komponencie
 * (subscribeToVersionSubfields, applyMassDataForCurrentVersion) może odróżnić
 * "zabudowa nie ma w ogóle takiego źródła danych" od "brak dopasowania w
 * tabeli, mimo że istnieje".
 *
 * @module vehicle-axle-mass-limits
 */

import { VehicleLine } from "../vehicle-line";
import {
  IVECO_AXLE_MASS_LIMITS,
  expandIvecoTonnageCandidates,
} from "./iveco-axle-mass-limits";
import { MERCEDES_TOURIST_LINE_HIGHDECK_AXLE_MASS_LIMITS } from "./mercedes-tourist-line-highdeck-axle-mass-limits";
import { MERCEDES_TOURIST_LINE_AXLE_MASS_LIMITS } from "./mercedes-tourist-line-axle-mass-limits";

export interface AxleMassLimit {
  axle1: number;
  axle2: number;
}

/**
 * Tabela dopuszczalnych mas na osiach dla jednej zabudowy: klucz główny ->
 * oznaczenie skrzyni/napędu (albo "*", gdy zabudowa go nie potrzebuje) -> wpis.
 * Klucz główny to zwykle tonaż ({{versionTonnage}}), ale dla zwykłego Mercedes
 * Benz Tourist Line jest to kod masy pojazdu ({{versionMassCode}}, "C"/"F") -
 * patrz getAxleMassLimit oraz mercedes-tourist-line-axle-mass-limits.ts.
 */
export type AxleMassLimitTable = Record<string, Record<string, AxleMassLimit>>;

/** Tabele dopuszczalnych mas na osiach zarejestrowane per zabudowa */
const AXLE_MASS_LIMIT_TABLES: Partial<Record<VehicleLine, AxleMassLimitTable>> =
  {
    [VehicleLine.IvecoTouristLine]: IVECO_AXLE_MASS_LIMITS,
    [VehicleLine.MercedesTouristLineHighDeck]:
      MERCEDES_TOURIST_LINE_HIGHDECK_AXLE_MASS_LIMITS,
    [VehicleLine.MercedesTouristLine]: MERCEDES_TOURIST_LINE_AXLE_MASS_LIMITS,
  };

/**
 * Niektóre zabudowy kodują w tabeli źródłowej "dziesiątkę" tonażu wieloznacznie
 * (np. Iveco: "8X" zamiast "80".."89" - patrz iveco-axle-mass-limits.ts). Dla
 * takich zabudów rejestrujemy tu funkcję generującą dodatkowe kandydujące
 * klucze tonażu do przeszukania w tabeli, poza dokładnym dopasowaniem.
 */
const TONNAGE_CANDIDATE_EXPANDERS: Partial<
  Record<VehicleLine, (tonnage: string) => string[]>
> = {
  [VehicleLine.IvecoTouristLine]: expandIvecoTonnageCandidates,
};

/** Czy dla podanej zabudowy mamy zarejestrowaną (niepustą) tabelę dopuszczalnych mas na osiach */
export function hasAxleMassLimitTable(line: VehicleLine): boolean {
  const table = AXLE_MASS_LIMIT_TABLES[line];
  return !!table && Object.keys(table).length > 0;
}

/**
 * Wyznacza dopuszczalne masy na osiach dla podanej zabudowy, tonażu i
 * (o ile dana zabudowa go rozróżnia) oznaczenia skrzyni/napędu. Jeśli nie
 * znaleziono dopasowania dla dokładnego oznaczenia (albo w ogóle go nie
 * podano), korzysta z wpisu "*" (gdy oznaczenie skrzyni/napędu nie wpływa na
 * wynik dla tej zabudowy - patrz Mercedes HighDeck).
 *
 * @param line - Zabudowa (VehicleLine)
 * @param tonnage - Klucz główny tabeli: kod tonażu (np. "72", "82", "80"),
 *   a dla zwykłego Mercedes Benz Tourist Line kod masy pojazdu ("C"/"F")
 * @param gearboxMarker - Dwuznakowe oznaczenie z {{baseVehicleVersion}} (np. "AM"), o ile dotyczy
 * @returns Dopuszczalne masy na osiach lub null, jeśli nie znaleziono dopasowania
 *   (albo zabudowa w ogóle nie ma zarejestrowanej tabeli)
 */
export function getAxleMassLimit(
  line: VehicleLine,
  tonnage: string | undefined | null,
  gearboxMarker: string | undefined | null,
): AxleMassLimit | null {
  if (!tonnage) return null;

  const table = AXLE_MASS_LIMIT_TABLES[line];
  if (!table) return null;

  const expandCandidates = TONNAGE_CANDIDATE_EXPANDERS[line];
  const candidates = expandCandidates ? expandCandidates(tonnage) : [tonnage];

  for (const candidate of candidates) {
    const entriesForTonnage = table[candidate];
    if (!entriesForTonnage) continue;
    const entry =
      (gearboxMarker ? entriesForTonnage[gearboxMarker] : undefined) ??
      entriesForTonnage["*"];
    if (entry) return entry;
  }

  return null;
}
